/**
 * useExternalAnalytics Hook
 * 
 * Consent-aware analytics tracking for GA4 and Meta Pixel.
 * Integrates with existing useConsentPreferences hook.
 * Respects COPPA requirements (no Meta tracking for minors).
 * 
 * @example
 * const { trackGA4, trackMeta, trackBoth } = useExternalAnalytics();
 * trackBoth('sign_up_complete', { method: 'email', account_type: 'parent' });
 */

import { useCallback } from 'react';
import { useConsentPreferences } from '@/hooks/useConsentPreferences';
import { getCurrentUTMParams } from '@/lib/utmPreserver';
import {
  ANALYTICS_CONFIG,
  ANALYTICS_EVENTS,
  GA4_STANDARD_MIRROR,
  getContentGroup,
  sanitizeEventParams,
  type AnalyticsEventName,
} from '@/lib/analyticsConfig';
import { trackMetaEvent, generateEventId, isMetaPixelLoaded } from '@/lib/metaPixel';
import { logger } from '@/lib/logger';

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
    dataLayer: unknown[];
  }
}

/**
 * Check if GA4 gtag is loaded
 */
function isGtagLoaded(): boolean {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
}

export function useExternalAnalytics() {
  const { preferences, isCoppaAge, hasConsented } = useConsentPreferences();

  /**
   * Check if GA4 tracking is allowed
   */
  const canTrackGA4 = useCallback((): boolean => {
    return hasConsented && preferences?.analytics_cookies === true;
  }, [hasConsented, preferences?.analytics_cookies]);

  /**
   * Check if Meta tracking is allowed (includes COPPA protection)
   */
  const canTrackMeta = useCallback((): boolean => {
    // Never track Meta for COPPA-age users
    if (isCoppaAge) {
      return false;
    }
    return hasConsented && preferences?.marketing_cookies === true;
  }, [hasConsented, preferences?.marketing_cookies, isCoppaAge]);

  /**
   * Track a GA4 event
   */
  const trackGA4 = useCallback(
    (eventName: AnalyticsEventName | string, params?: Record<string, unknown>) => {
      // Consent Mode v2 (configured in index.html with denied defaults) handles
      // cookieless transmission until the user grants analytics_storage. We must
      // NOT block events client-side — Google upgrades to full mode automatically
      // once consent is granted. Hard-blocking here would silently drop the
      // entire funnel for any visitor who hasn't accepted analytics cookies.
      if (!isGtagLoaded()) {
        if (ANALYTICS_CONFIG.debugMode) {
          logger.debug('[GA4] gtag not loaded, dropping event', { eventName });
        }
        return;
      }

      try {
        // Get current UTM params for attribution
        const utmParams = getCurrentUTMParams();
        
        // Get content group from current path
        const contentGroup = typeof window !== 'undefined' 
          ? getContentGroup(window.location.pathname)
          : undefined;

        // Sanitize and combine params
        const sanitizedParams = sanitizeEventParams({
          ...params,
          ...utmParams,
          content_group: contentGroup,
          page_location: typeof window !== 'undefined' ? window.location.href : undefined,
          page_title: typeof document !== 'undefined' ? document.title : undefined,
        });

        window.gtag('event', eventName, sanitizedParams);

        if (ANALYTICS_CONFIG.debugMode) {
          logger.debug('[GA4] Event tracked', { eventName, params: sanitizedParams });
        }
      } catch (error) {
        logger.debug('[GA4] Error tracking event', error);
      }
    },
    [canTrackGA4]
  );

  /**
   * Track a Meta Pixel event (with COPPA protection)
   */
  const trackMeta = useCallback(
    (eventName: AnalyticsEventName | string, params?: Record<string, unknown>) => {
      if (!canTrackMeta()) {
        if (ANALYTICS_CONFIG.debugMode) {
          logger.debug('[Meta] Tracking blocked (consent or COPPA)', { eventName });
        }
        return;
      }

      // Get current UTM params for attribution
      const utmParams = getCurrentUTMParams();
      
      // Generate event ID for deduplication
      const eventId = generateEventId();

      trackMetaEvent(eventName, { ...params, ...utmParams }, { eventId });
    },
    [canTrackMeta]
  );

  /**
   * Track an event to both GA4 and Meta Pixel (respecting consent)
   */
  const trackBoth = useCallback(
    (eventName: AnalyticsEventName | string, params?: Record<string, unknown>) => {
      trackGA4(eventName, params);
      trackMeta(eventName, params);

      // GA4 standard-event mirror (e.g. sign_up_complete -> sign_up,
      // request_demo_submit -> generate_lead). Fires GA4 only; Meta
      // already has its own standard mapping via META_EVENT_MAPPING.
      const mirror = GA4_STANDARD_MIRROR[eventName as string];
      if (mirror) {
        const mirroredParams = { ...(params ?? {}), ...(mirror.extra ?? {}) };
        // GA4 conversion reports for `generate_lead` expect value+currency.
        if (mirror.name === 'generate_lead') {
          if (mirroredParams.value === undefined) mirroredParams.value = 0;
          if (mirroredParams.currency === undefined) mirroredParams.currency = 'USD';
        }
        // Lightweight required-param validation (dev only).
        if (ANALYTICS_CONFIG.debugMode) {
          if (mirror.name === 'sign_up' && !('method' in mirroredParams)) {
            logger.debug('[GA4] sign_up missing `method` param', mirroredParams);
          }
          if (mirror.name === 'generate_lead' && !('lead_type' in mirroredParams)) {
            logger.debug('[GA4] generate_lead missing `lead_type` param', mirroredParams);
          }
        }
        trackGA4(mirror.name, mirroredParams);
      }
    },
    [trackGA4, trackMeta]
  );

  /**
   * Fire an event exactly once per (eventName, dedupKey) per browser session.
   * Guards against React StrictMode double-mount, refreshes, and duplicate
   * webhook arrivals for purchase/redemption/assignment milestones.
   */
  const trackOnce = useCallback(
    (
      eventName: AnalyticsEventName | string,
      dedupKey: string,
      params?: Record<string, unknown>,
    ) => {
      try {
        if (typeof window === 'undefined') return;
        const storageKey = `ga4_dedup:${eventName}:${dedupKey}`;
        if (window.sessionStorage.getItem(storageKey)) {
          if (ANALYTICS_CONFIG.debugMode) {
            logger.debug('[Analytics] trackOnce deduped', { eventName, dedupKey });
          }
          return;
        }
        window.sessionStorage.setItem(storageKey, '1');
      } catch {
        // sessionStorage may be unavailable (privacy mode); still fire to avoid loss.
      }
      trackBoth(eventName, params);
    },
    [trackBoth],
  );

  /**
   * Track page view to both platforms
   */
  const trackPageView = useCallback(
    (pagePath?: string, pageTitle?: string) => {
      const path = pagePath || (typeof window !== 'undefined' ? window.location.pathname : '');
      const title = pageTitle || (typeof document !== 'undefined' ? document.title : '');

      trackBoth(ANALYTICS_EVENTS.PAGE_VIEW, {
        page_path: path,
        page_title: title,
      });
    },
    [trackBoth]
  );

  /**
   * Track landing page views with proper event names
   */
  const trackLandingView = useCallback(
    (landingType: 'callback_zone' | 'coach' | 'castings' | 'pro' | 'hmu' | 'crew' | 'draftline' | 'capture' | 'pricing' | 'persona' | 'comparison', additionalParams?: Record<string, unknown>) => {
      const eventMap: Record<string, AnalyticsEventName> = {
        callback_zone: ANALYTICS_EVENTS.VIEW_LANDING_CALLBACK_ZONE,
        coach: ANALYTICS_EVENTS.VIEW_LANDING_COACH,
        castings: ANALYTICS_EVENTS.VIEW_LANDING_CASTINGS,
        pro: ANALYTICS_EVENTS.VIEW_LANDING_PRO,
        hmu: ANALYTICS_EVENTS.VIEW_LANDING_HMU,
        crew: ANALYTICS_EVENTS.VIEW_LANDING_CREW,
        draftline: ANALYTICS_EVENTS.VIEW_LANDING_DRAFTLINE,
        capture: ANALYTICS_EVENTS.VIEW_LANDING_CAPTURE,
        pricing: ANALYTICS_EVENTS.VIEW_PRICING,
        persona: ANALYTICS_EVENTS.VIEW_PERSONA_LANDING,
        comparison: ANALYTICS_EVENTS.VIEW_COMPARISON_PAGE,
      };

      const eventName = eventMap[landingType];
      if (eventName) {
        trackBoth(eventName, {
          page_path: typeof window !== 'undefined' ? window.location.pathname : '',
          ...additionalParams,
        });
      }
    },
    [trackBoth]
  );

  /**
   * Track signup events
   */
  const trackSignup = useCallback(
    (stage: 'start' | 'complete', params: { method?: string; account_type?: string; partner_id?: string; affiliate_code?: string }) => {
      const eventName = stage === 'start' ? ANALYTICS_EVENTS.SIGN_UP_START : ANALYTICS_EVENTS.SIGN_UP_COMPLETE;
      trackBoth(eventName, params);
    },
    [trackBoth]
  );

  /**
   * Track subscription events
   */
  const trackSubscription = useCallback(
    (stage: 'view_offer' | 'start_trial' | 'subscribe', params: {
      plan_name?: string;
      platform?: string;
      billing_period?: string;
      value?: number;
      currency?: string;
      partner_id?: string;
    }) => {
      const eventMap: Record<string, AnalyticsEventName> = {
        view_offer: ANALYTICS_EVENTS.VIEW_SUBSCRIPTION_OFFER,
        start_trial: ANALYTICS_EVENTS.START_TRIAL,
        subscribe: ANALYTICS_EVENTS.SUBSCRIBE_PREMIUM,
      };

      const eventName = eventMap[stage];
      if (eventName) {
        trackBoth(eventName, params);
      }
    },
    [trackBoth]
  );

  /**
   * Track box redemption flow events
   */
  const trackRedemption = useCallback(
    (stage: 'qr_scan' | 'landing_view' | 'start' | 'complete', params: {
      box_code?: string;
      qr_campaign_id?: string;
      partner_id?: string;
      redemption_type?: string;
      subscription_granted?: boolean;
      discount_applied?: boolean;
    }) => {
      const eventMap: Record<string, AnalyticsEventName> = {
        qr_scan: ANALYTICS_EVENTS.QR_SCAN,
        landing_view: ANALYTICS_EVENTS.REDEMPTION_LANDING_VIEW,
        start: ANALYTICS_EVENTS.REDEMPTION_START,
        complete: ANALYTICS_EVENTS.REDEMPTION_COMPLETE,
      };

      const eventName = eventMap[stage];
      if (eventName) {
        trackBoth(eventName, params);
      }
    },
    [trackBoth]
  );

  /**
   * Track partner attribution
   */
  const trackPartnerReferral = useCallback(
    (params: { partner_id: string; partner_type?: string; campaign_id?: string }) => {
      trackBoth(ANALYTICS_EVENTS.PARTNER_REFERRAL_CLICK, params);
    },
    [trackBoth]
  );

  /**
   * Track activation events
   */
  const trackActivation = useCallback(
    (eventType: 'start_practice' | 'complete_practice' | 'earn_badge' | 'parent_view' | 'first_ai', params?: Record<string, unknown>) => {
      const eventMap: Record<string, AnalyticsEventName> = {
        start_practice: ANALYTICS_EVENTS.START_PRACTICE_PLAN,
        complete_practice: ANALYTICS_EVENTS.COMPLETE_PRACTICE_PLAN,
        earn_badge: ANALYTICS_EVENTS.EARN_BADGE,
        parent_view: ANALYTICS_EVENTS.PARENT_DASHBOARD_VIEW,
        first_ai: ANALYTICS_EVENTS.FIRST_AI_FEATURE_USE,
      };

      const eventName = eventMap[eventType];
      if (eventName) {
        trackBoth(eventName, params);
      }
    },
    [trackBoth]
  );

  /**
   * Track B2B form submissions
   */
  const trackB2BSubmit = useCallback(
    (formType: 'demo' | 'contact' | 'partner_apply', params?: Record<string, unknown>) => {
      const eventMap: Record<string, AnalyticsEventName> = {
        demo: ANALYTICS_EVENTS.REQUEST_DEMO_SUBMIT,
        contact: ANALYTICS_EVENTS.CONTACT_SALES_SUBMIT,
        partner_apply: ANALYTICS_EVENTS.PARTNER_APPLY_SUBMIT,
      };

      const eventName = eventMap[formType];
      if (eventName) {
        trackBoth(eventName, params);
      }
    },
    [trackBoth]
  );

  return {
    // Core tracking functions
    trackGA4,
    trackMeta,
    trackBoth,
    trackOnce,
    trackPageView,
    
    // Convenience tracking functions
    trackLandingView,
    trackSignup,
    trackSubscription,
    trackRedemption,
    trackPartnerReferral,
    trackActivation,
    trackB2BSubmit,
    
    // Status
    canTrackGA4: canTrackGA4(),
    canTrackMeta: canTrackMeta(),
    isGA4Loaded: isGtagLoaded(),
    isMetaLoaded: isMetaPixelLoaded(),
  };
}

export default useExternalAnalytics;
